import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, MapPinCheck, MapPinOff, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { useDebounce } from "@/hooks/useDebounce";
import { searchAddresses } from "@/lib/geo";
import { cn } from "@/lib/utils";

/**
 * AddressAutocomplete – adresse-felt med Kartverket-forslag (formatert adresse + koordinatar).
 *
 * value    : { address, postal_code, city, lat, lng, source }  (address kan vere fritekst utan koordinatar)
 * onChange : (nextValue) => void
 * Manuell fallback: det du skriv blir lagra som adresse sjølv om du ikkje vel frå lista (utan koordinatar).
 */
export const AddressAutocomplete = ({ value, onChange, placeholder = "Gate og nummer, stad …", testid = "address-autocomplete", disabled, className, inputClassName, autoFocus }) => {
  const [text, setText] = useState(value?.address || "");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(-1);
  const dirty = useRef(false);
  const lastEmitted = useRef(value?.address || "");
  const debounced = useDebounce(text, 280);

  // Sync from outside (t.d. Brønnøysund-treff fyller adressa) – ignorer ekko av det vi sjølv nettopp sende
  useEffect(() => {
    const incoming = value?.address || "";
    if (incoming === lastEmitted.current) return;
    lastEmitted.current = incoming;
    setText(incoming);
    dirty.current = false;
  }, [value?.address]);

  useEffect(() => {
    if (!dirty.current || debounced.trim().length < 3) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setBusy(true);
    searchAddresses(debounced, 7)
      .then((r) => {
        if (cancelled) return;
        setResults(r);
        setOpen(r.length > 0);
        setActive(-1);
      })
      .catch(() => !cancelled && setResults([]))
      .finally(() => !cancelled && setBusy(false));
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const pick = (hit) => {
    dirty.current = false;
    setOpen(false);
    setResults([]);
    setText(hit.address);
    lastEmitted.current = hit.address;
    onChange?.({ address: hit.address, postal_code: hit.postal_code, city: hit.city, lat: hit.lat, lng: hit.lng, source: "kartverket", approximate: false });
  };

  const onInput = (e) => {
    const t = e.target.value;
    dirty.current = true;
    setText(t);
    lastEmitted.current = t;
    // Fritekst = manuell adresse utan koordinatar (backend prøver å geokode ved lagring)
    onChange?.({ address: t, postal_code: null, city: null, lat: null, lng: null, source: t ? "manual" : null, approximate: false });
  };

  const clear = () => {
    dirty.current = false;
    setText("");
    setResults([]);
    setOpen(false);
    lastEmitted.current = "";
    onChange?.({ address: "", postal_code: null, city: null, lat: null, lng: null, source: null, approximate: false });
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a <= 0 ? results.length - 1 : a - 1));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      pick(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const hasCoords = value?.lat != null && value?.lng != null;
  const hasText = !!(value?.address || "").trim();

  return (
    <div className={cn("space-y-1.5", className)} data-testid={testid}>
      <Popover open={open && results.length > 0} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={text}
              onChange={onInput}
              onKeyDown={onKeyDown}
              onFocus={() => results.length > 0 && setOpen(true)}
              placeholder={placeholder}
              disabled={disabled}
              autoFocus={autoFocus}
              autoComplete="off"
              role="combobox"
              aria-expanded={open}
              aria-autocomplete="list"
              className={cn("h-11 pl-9 pr-9", inputClassName)}
              data-testid={`${testid}-input`}
            />
            <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center">
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : hasText && !disabled ? (
                <button type="button" onClick={clear} aria-label="Tøm adresse" className="grid h-7 w-7 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground" data-testid={`${testid}-clear`}>
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </span>
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            // Klikk i input skal ikkje lukke lista
            if (e.target?.closest?.(`[data-testid="${testid}"]`)) e.preventDefault();
          }}
          className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-1"
          data-testid={`${testid}-results`}
        >
          <ul role="listbox" className="max-h-64 overflow-y-auto">
            {results.map((h, i) => (
              <li key={`${h.address}-${i}`} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(h)}
                  onMouseEnter={() => setActive(i)}
                  className={cn(
                    "flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
                    i === active ? "bg-accent" : "hover:bg-accent/70"
                  )}
                  data-testid={`${testid}-option-${i}`}
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{h.street || h.address}</span>
                    <span className="block truncate text-xs text-muted-foreground">{[h.postal_code, h.city].filter(Boolean).join(" ")}{h.municipality ? ` · ${h.municipality}` : ""}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t px-2.5 py-1.5 text-[11px] text-muted-foreground">Adresser frå Kartverket</div>
        </PopoverContent>
      </Popover>

      {hasText && (
        <p className={cn("flex items-center gap-1.5 text-xs", hasCoords ? "text-[rgb(var(--status-ok))]" : "text-muted-foreground")} data-testid={`${testid}-status`}>
          {hasCoords ? <MapPinCheck className="h-3.5 w-3.5" /> : <MapPinOff className="h-3.5 w-3.5" />}
          {hasCoords
            ? value?.approximate ? "Omtrentleg plassering (postnummer) – vel gjerne ei gateadresse." : "Koordinatar funne – blir vist på kartet."
            : "Ingen koordinatar enno – vel frå lista, elles prøver vi å finne adressa når du lagrar."}
        </p>
      )}
    </div>
  );
};
